import React, { useState, useEffect, useRef } from 'react';
import './BlackberryProductSlider.scss';

const BlackberryProductSlider = () => {
    const [currentIndex, setCurrentIndex] = useState(0);
    const [isTransitioning, setIsTransitioning] = useState(false);
    const [touchStart, setTouchStart] = useState(0);
    const [touchEnd, setTouchEnd] = useState(0);
    const sliderRef = useRef(null);

    // Sample product images - replace with your actual product data
    const products = [
        '/assets/img/elnora/1.png',
        '/assets/img/elnora/2.png',
        '/assets/img/elnora/3.png',
        '/assets/img/elnora/4.png',
        '/assets/img/elnora/5.png',
        '/assets/img/elnora/6.png',
        '/assets/img/elnora/7.png',
        '/assets/img/elnora/8.png',
        '/assets/img/elnora/9.png',
        '/assets/img/elnora/10.png',
        '/assets/img/elnora/11.png'
    ];

    // Create extended array for infinite scroll (triple the products)
    const extendedProducts = [...products, ...products, ...products];

    const [primaryImage, setPrimaryImage] = useState(products[0]);

    // Initialize to middle section
    useEffect(() => {
        setCurrentIndex(products.length);
    }, []);

    // Handle transition end - reset to middle section if needed
    const handleTransitionEnd = () => {
        setIsTransitioning(false);
        
        if (sliderRef.current) {
            // If we've gone past the last set, jump back to middle set
            if (currentIndex >= products.length * 2) {
                sliderRef.current.style.transition = 'none';
                setCurrentIndex(products.length);
                setTimeout(() => {
                    if (sliderRef.current) {
                        sliderRef.current.style.transition = 'transform 0.5s ease';
                    }
                }, 50);
            } 
            // If we've gone before the first set, jump to middle set
            else if (currentIndex < products.length) {
                sliderRef.current.style.transition = 'none';
                setCurrentIndex(products.length * 2 - 1);
                setTimeout(() => {
                    if (sliderRef.current) {
                        sliderRef.current.style.transition = 'transform 0.5s ease';
                    }
                }, 50);
            }
        }
    };

    // Handle next slide
    const nextSlide = () => {
        if (isTransitioning) return;
        setIsTransitioning(true);
        setCurrentIndex((prevIndex) => prevIndex + 1);
    };

    // Handle previous slide
    const prevSlide = () => {
        if (isTransitioning) return;
        setIsTransitioning(true);
        setCurrentIndex((prevIndex) => prevIndex - 1);
    };

    // Get actual index in original products array
    const getActualIndex = (index) => {
        return index % products.length;
    };

    // Touch handlers for mobile swipe
    const handleTouchStart = (e) => {
        setTouchStart(e.targetTouches[0].clientX);
    };

    const handleTouchMove = (e) => {
        setTouchEnd(e.targetTouches[0].clientX);
    };

    const handleTouchEnd = () => {
        if (!touchStart || !touchEnd) return;
        
        const distance = touchStart - touchEnd;
        const isLeftSwipe = distance > 50;
        const isRightSwipe = distance < -50;

        if (isLeftSwipe) {
            nextSlide();
        }
        if (isRightSwipe) {
            prevSlide();
        }

        // Reset touch values
        setTouchStart(0);
        setTouchEnd(0);
    };


    return (
        <div className='bb-product-slider'>

            <div className='bb-product-slider-primary'>
                <h3>Explore our Festive Collection</h3>
                <p>Make the festival memorable with our curated products</p>
                <button>Shop Now</button>
            </div>
            
            <div className='bb-product-slider-secondary'>
                <div 
                    className='bb-slider-container'
                    onTouchStart={handleTouchStart}
                    onTouchMove={handleTouchMove}
                    onTouchEnd={handleTouchEnd}
                >
                    <div 
                        ref={sliderRef}
                        className='bb-slider-track' 
                        onTransitionEnd={handleTransitionEnd}
                    >
                        {/* Desktop View - 3 Images */}
                        <div className='bb-slider-desktop' style={{ transform: `translateX(-${currentIndex * (100 / 3)}%)` }}>
                            {extendedProducts.map((product, index) => (
                                <div 
                                    key={`desktop-${index}`}
                                    className='bb-slider-item'
                                    onClick={() => setPrimaryImage(product)}
                                >
                                    <img 
                                        src={product} 
                                        alt={`Product ${getActualIndex(index) + 1}`}
                                        className='bb-slider-image'
                                    />
                                </div>
                            ))}
                        </div>

                        {/* Tablet View - 2 Images */}
                        <div className='bb-slider-tablet' style={{ transform: `translateX(-${currentIndex * 50}%)` }}>
                            {extendedProducts.map((product, index) => (
                                <div 
                                    key={`tablet-${index}`}
                                    className='bb-slider-item'
                                    onClick={() => setPrimaryImage(product)}
                                >
                                    <img 
                                        src={product} 
                                        alt={`Product ${getActualIndex(index) + 1}`}
                                        className='bb-slider-image'
                                    />
                                </div>
                            ))}
                        </div>

                        {/* Mobile View - Single Image */}
                        <div className='bb-slider-mobile' style={{ transform: `translateX(-${currentIndex * 100}%)` }}>
                            {extendedProducts.map((product, index) => (
                                <div 
                                    key={`mobile-${index}`}
                                    className='bb-slider-item'
                                >
                                    <img 
                                        src={product} 
                                        alt={`Product ${getActualIndex(index) + 1}`}
                                        className='bb-slider-image'
                                    />
                                </div>
                            ))}
                        </div>
                    </div>
                </div>

                <button 
                    className='bb-slider-btn bb-slider-btn-next'
                    onClick={nextSlide}
                    disabled={isTransitioning}
                    aria-label="Next products"
                >
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                        <path d="M9 18L15 12L9 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                </button>
            </div>

            {/* Mobile Indicators */}
            <div className='bb-slider-indicators'>
                {products.map((_, index) => (
                    <button
                        key={index}
                        className={`bb-indicator ${getActualIndex(currentIndex) === index ? 'active' : ''}`}
                        onClick={() => setCurrentIndex(products.length + index)}
                        aria-label={`Go to slide ${index + 1}`}
                    />
                ))}
            </div>
        </div>
    );
};

export default BlackberryProductSlider;